// localStorage-backed settings + per-game progress. No PII.
//
// History is keyed by the date-seed ("YYYY-MM-DD"). Stats (streak / played / win% /
// distribution) are DERIVED from this map, so there is one source of truth and replaying
// the archive can't corrupt the streak (we only count true daily plays for the streak).

const PREFIX = 'studio:';

export function loadSetting<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    return raw === null ? fallback : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

export function saveSetting<T>(key: string, value: T): void {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify(value));
  } catch {
    /* storage may be unavailable (private mode); fail silently */
  }
}

export interface DayResult {
  seed: string;
  solved: boolean;
  tries: number;
  /** When recorded (epoch ms). */
  ts?: number;
  /** True only for the live daily (not an archive replay). Streak counts these. */
  daily?: boolean;
}

export type History = Record<string, DayResult>;

/** Record a result for a game keyed by its date-seed and return nothing. */
export function recordResult(game: string, result: DayResult): void {
  const r: DayResult = { ts: Date.now(), ...result };
  saveSetting(`${game}:last`, r);
  const history = loadSetting<History>(`${game}:history`, {});
  // Don't let an archive replay overwrite a real daily record's `daily` flag.
  const prev = history[r.seed];
  if (prev?.daily && !r.daily) r.daily = true;
  history[r.seed] = r;
  saveSetting(`${game}:history`, history);
}

export function getResult(game: string, seed: string): DayResult | null {
  const history = loadSetting<History>(`${game}:history`, {});
  return history[seed] ?? null;
}

export function getHistory(game: string): History {
  return loadSetting<History>(`${game}:history`, {});
}
